'use client';

import { usePathname, useRouter } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import { useRef, useEffect, useState, useMemo } from 'react';
import type { CaseStudy as _CaseStudy } from '../../lib/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Keyboard, Mousewheel, Scrollbar } from 'swiper/modules';
import { trackEvent } from '@/lib/analytics';
import 'swiper/css';
import 'swiper/css/scrollbar';

interface RelatedStudy {
  slug: string;
  title: string;
  thumbnail: string;
  thumbnailVideo?: string; // Optional video for thumbnail - uses thumbnail image as fallback
}

interface RelatedStudiesProps {
  studies: RelatedStudy[];
  title?: string;
  className?: string;
}

/**
 * ThumbnailMedia - Renders either a video or image for the study card thumbnail
 * 
 * Video plays only on hover (muted and loops). If video fails to load or is not provided,
 * the thumbnail image is displayed instead. Shows poster/image by default
 * 
 * @param thumbnail - Static image URL (always shown as base layer)
 * @param thumbnailVideo - Optional video URL (plays on hover)
 * @param title - Alt text for accessibility
 * @param isHovered - Whether the card is currently hovered
 */
const ThumbnailMedia = ({ 
  thumbnail, 
  thumbnailVideo, 
  title,
  isHovered
}: { 
  thumbnail: string; 
  thumbnailVideo?: string; 
  title: string;
  isHovered: boolean;
}) => {
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasVideo = thumbnailVideo && !videoError;

  const handleVideoError = () => {
    setVideoError(true);
  };

  // Control video playback based on hover state
  useEffect(() => {
    if (!videoRef.current || !hasVideo) return;
    
    if (isHovered) {
      videoRef.current.play().catch(() => {
        // Autoplay might be blocked, fail silently
      });
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0; // Reset to beginning
    }
  }, [isHovered, hasVideo]);

  return (
    <>
      {/* Static image - always rendered as base layer */}
      <Image
        src={thumbnail}
        alt={title}
        fill
        className={`object-cover transition-transform duration-[1000ms] ease-[cubic-bezier(0.19,1,0.22,1)] md:group-hover:scale-125 ${hasVideo ? 'z-0' : 'z-10'}`}
        sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
      />
      
      {/* Video layer - only rendered if video exists, plays on hover */}
      {hasVideo && (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          onError={handleVideoError}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-[1000ms] ease-[cubic-bezier(0.19,1,0.22,1)] md:group-hover:scale-125 z-10 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
          aria-label={`${title} thumbnail video`}
        >
          <source src={thumbnailVideo} type="video/mp4" />
        </video>
      )}
    </>
  );
};

const StudyCard = ({ study, pathname, isDraggingRef, router }: { study: RelatedStudy; pathname: string; isDraggingRef: React.MutableRefObject<boolean>; router: ReturnType<typeof useRouter> }) => {
  const linkRef = useRef<HTMLAnchorElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // If dragging, don't navigate
    if (isDraggingRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    trackEvent('our_work_click', {
      click_text: study.title,
      click_url: `/case-studies/${study.slug}`,
      page_path: pathname
    });
    
    // Programmatically click the hidden link to trigger PageTransition
    if (linkRef.current) {
      linkRef.current.click();
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="group block cursor-pointer overflow-hidden"
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (!isDraggingRef.current) {
            trackEvent('our_work_click', {
              click_text: study.title,
              click_url: `/case-studies/${study.slug}`,
              page_path: pathname
            });
            // Programmatically click the hidden link to trigger PageTransition
            if (linkRef.current) {
              linkRef.current.click();
            }
          }
        }
      }}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
      aria-label={`View case study: ${study.title}`}
    >
    <article className="h-full flex flex-col">
      {/* Thumbnail - Video plays on hover, static image otherwise */}
      <div className="relative aspect-[4/3] overflow-hidden rounded-2xl mb-3 lg:mb-4 bg-gray-100">
        <ThumbnailMedia 
          thumbnail={study.thumbnail} 
          thumbnailVideo={study.thumbnailVideo} 
          title={study.title}
          isHovered={isHovered}
        />
        <div className="absolute inset-0 bg-black bg-opacity-0 md:group-hover:bg-opacity-30 transition-all duration-[1000ms] ease-[cubic-bezier(0.19,1,0.22,1)] rounded-2xl z-20" />
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col">
        {/* Title */}
        <h3 className="card-title-lg pb-1">
          {study.title}
        </h3>
      </div>
    </article>
    {/* Hidden link to trigger PageTransition */}
    <Link 
      ref={linkRef}
      href={`/case-studies/${study.slug}`}
      style={{ display: 'none' }}
      aria-hidden="true"
      tabIndex={-1}
    />
    </div>
  );
};

export default function RelatedStudies({ 
  studies, 
  title = "More case studies",
  className = "" 
}: RelatedStudiesProps) {
  const pathname = usePathname();
  const router = useRouter();
  const isDraggingRef = useRef<boolean>(false);
  const dragTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const swiperRef = useRef<SwiperType | null>(null);
  
  if (!studies || studies.length === 0) {
    return null;
  }

  const displayStudies = useMemo(() => {
    const shuffledStudies = [...studies];

    for (let i = shuffledStudies.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledStudies[i], shuffledStudies[j]] = [shuffledStudies[j], shuffledStudies[i]];
    }

    return shuffledStudies.slice(0, 3);
  }, [studies]);

  // Handle both touch and mouse drag start
  const handleDragStart = () => {
    isDraggingRef.current = false;
    // Clear any pending timeout
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }
  };

  // Handle slider movement (both touch and mouse)
  const handleSliderMove = () => {
    isDraggingRef.current = true;
  };

  // Handle drag end
  const handleDragEnd = () => {
    // Keep dragging flag true briefly to prevent click events
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }
    dragTimeoutRef.current = setTimeout(() => {
      isDraggingRef.current = false;
    }, 100); // Reduced from 500ms to 100ms for better responsiveness
  };

  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
    };
  }, []);

  return (
    <section className={`related-studies ${className}`}>
      <div className="vs-article"></div>
      <div className="">
        {/* Section Header */}
        <div className="pb-[1.88rem] lg:pb-[3.75rem]">
          <h2 className="heading-2 mt-[0.44rem] lg:mt-[2.125rem] mb-5 lg:mb-[1.5rem]">
            {title}
          </h2>
          <hr className="border-black" />
        </div>

        {/* Swiper Carousel */}
        <div className="related-studies-carousel overflow-hidden">
          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            modules={[Keyboard, Mousewheel, Scrollbar]}
            spaceBetween={32}
            grabCursor={true}
            preventClicks={true}
            preventClicksPropagation={true}
            slideToClickedSlide={false}
            allowTouchMove={true}
            threshold={5}
            keyboard={{
              enabled: true,
            }}
            mousewheel={{
              forceToAxis: true,
              sensitivity: 1,
              releaseOnEdges: true,
            }}
            scrollbar={{
              hide: false,
              draggable: true,
              dragSize: 'auto'
            }}
            onTouchStart={handleDragStart}
            onSliderMove={handleSliderMove}
            onTouchEnd={handleDragEnd}
            breakpoints={{
              320: {
                slidesPerView: 1,
                spaceBetween: 20,
              },
              768: {
                slidesPerView: 2,
                spaceBetween: 25,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 32,
              },
            }}
            className="w-full"
            role="region"
            aria-label="Related case studies carousel"
          >
            {displayStudies.map((study) => (
              <SwiperSlide key={study.slug}>
                <StudyCard study={study} pathname={pathname} isDraggingRef={isDraggingRef} router={router} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      <style jsx global>{`
        .related-studies-carousel .swiper {
          overflow: visible;
        }

        .related-studies-carousel .swiper-slide {
          width: auto;
          height: auto;
        }

        .related-studies-carousel .swiper-scrollbar {
          display: block;
          background: rgba(0, 0, 0, 0.1);
          border-radius: 0.625rem;
          margin: 2rem auto 0 auto;
          height: 0.375rem;
          position: relative !important;
          width: 80% !important;
          left: auto !important;
          transform: none !important;
        }

        @media (min-width: 768px) {
          .related-studies-carousel .swiper-scrollbar {
            /* margin: 60px auto 0 auto; */
            width: 50% !important;
          }
        }

        .related-studies-carousel .swiper-scrollbar-drag {
          background: #000000;
          border-radius: 0.625rem;
          height: 0.375rem !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
          position: relative;
        }

        .related-studies-carousel .swiper-scrollbar-drag::after {
          content: '';
          position: absolute;
          top: -0.96875rem;
          left: 0;
          right: 0;
          height: 2.5rem;
          background: transparent;
          z-index: 999;
          pointer-events: auto;
        }

        .related-studies-carousel .swiper-scrollbar-drag:hover {
          background: rgba(0, 0, 0, 0.7);
        }
      `}</style>
    </section>
  );
}